/**
 * The Values row's dots, below 930.
 *
 * The reference drives its row with a script: the cards are clipped with
 * `overflow: hidden`, moved by a transform, and three dots underneath page
 * through them. Ours is a scroll container with snap points, which swipes with
 * the platform's own momentum and keeps the keyboard and screen-reader
 * behaviour a scroller already has. The dots sit on top of that rather than in
 * place of it: pressing one scrolls to its card, and scrolling the row marks
 * the dot that matches. Same thing to look at, less to go wrong.
 *
 * Nothing is drawn until the script runs, so a browser that never gets here has
 * a row it can still swipe. Above the breakpoint the dots are removed outright
 * rather than hidden, since there is nothing there to page through.
 *
 * Scroll is heard on the row itself. The page's own scroller does not come into
 * it — this is the only element that moves sideways.
 */

const MOBILE = '(max-width: 929px)';

class ValuesDots extends HTMLElement {
  #dots = [];
  #cards = [];
  #ticking = false;

  connectedCallback() {
    this.row = this.querySelector('.values__grid');
    if (!this.row) return;

    this.mobile = window.matchMedia(MOBILE);
    this.mobile.addEventListener('change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.mobile?.removeEventListener('change', this.#sync);
    this.#teardown();
  }

  #sync = () => {
    if (this.mobile.matches) this.#build();
    else this.#teardown();
  };

  #build() {
    this.#cards = Array.from(this.row.querySelectorAll('.values__item'));
    if (this.#cards.length < 2 || this.nav) return;

    const nav = document.createElement('div');
    nav.className = 'values__dots';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Choose a value to read');

    this.#dots = this.#cards.map((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'values__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to ${i + 1} of ${this.#cards.length}`);
      dot.addEventListener('click', () => {
        // `start` matches the snap alignment, so the card lands on its inset.
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
      nav.append(dot);
      return dot;
    });

    /* A sibling of the row, never inside a card: the reference keeps its dots
       in an element of their own beside the moving track, measured at the same
       place on all three slides while the track travelled -360 and -720. Put
       inside a card they ride away with it and are gone by the third.

       The pair go in a wrapper of their own. The row cannot be the frame they
       are placed against — it is the scroll container, so anything positioned
       against it travels with the cards — and the band's inner box cannot
       either, since that starts above the heading. The wrapper starts exactly
       at the top of the pictures, which is what the placement is measured
       from. */
    const frame = document.createElement('div');
    frame.className = 'values__viewport';
    this.row.before(frame);
    frame.append(this.row, nav);
    this.nav = nav;
    this.row.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.#measure();
    this.#mark();
  }

  #teardown() {
    this.row?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    const frame = this.querySelector('.values__viewport');
    if (frame && this.row) {
      frame.before(this.row);
      frame.remove();
    }
    this.nav?.remove();
    this.nav = null;
    this.#dots = [];
  }

  #onResize = () => {
    this.#measure();
    this.#onScroll();
  };

  /*
   * The stylesheet places the dots a share of the way down the picture, so it
   * needs the picture's height. It cannot work that out on its own: a `top`
   * percentage resolves against the card, which is the picture plus all the
   * writing beneath it, and dividing by the shape setting inside `calc` put
   * the dots 300px too low when it was tried. A measured pixel height is
   * unambiguous. Re-read on resize, and again once the picture has loaded,
   * since an image with no file yet reports no height.
   */
  #measure() {
    const image = this.#cards[0]?.querySelector('.values__image');
    if (!image) return;
    const apply = () => this.style.setProperty('--values-image-h', `${Math.round(image.getBoundingClientRect().height)}px`);
    apply();
    if (image.tagName === 'IMG' && !image.complete) image.addEventListener('load', apply, { once: true });
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#mark();
    });
  };

  /* The card nearest the row's left inset is the one being read. Measured
     against the row rather than a running scroll total, so a resize or a
     changed card width needs no bookkeeping. */
  #mark() {
    if (!this.#dots.length) return;
    const edge = this.row.getBoundingClientRect().left;
    let nearest = 0;
    let best = Infinity;
    this.#cards.forEach((card, i) => {
      const distance = Math.abs(card.getBoundingClientRect().left - edge);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === nearest ? 'true' : 'false');
      dot.classList.toggle('is-current', i === nearest);
    });
  }
}

if (!customElements.get('values-dots')) {
  customElements.define('values-dots', ValuesDots);
}
